import React, { useState } from "react";
import Icon from "../shared/Icon";
import { NavLink } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { logout } from "../../../store/Actions/authActions";
import { MANAGER_ROUTES } from "../../../utils/managerRouting";
import useManagerScope from "../../../hooks/useManagerScope";
import LanguagePicker from "../shared/LanguagePicker";
import { initialsOf } from "../shared/format";
import "../manager.css";

const NAV_ITEMS = [
  { key: "Overzicht", to: MANAGER_ROUTES.overview, icon: "home-outline", exact: true },
  { key: "Bestellingen", to: MANAGER_ROUTES.orders, icon: "cube-outline" },
  { key: "Bewoners", to: MANAGER_ROUTES.residents, icon: "people-outline" },
  { key: "Etiketten", to: MANAGER_ROUTES.labels, icon: "qr-code-outline" },
  { key: "Maandoverzicht", to: MANAGER_ROUTES.monthly, icon: "document-text-outline" },
  { key: "Facturatie", to: MANAGER_ROUTES.invoicing, icon: "receipt-outline" },
];

/**
 * Coque de l'espace CP manager : sidebar fixe + zone de contenu.
 *
 * Le rôle étant administratif, la seule sortie est la déconnexion — il n'y a
 * volontairement aucun lien vers l'app client.
 */
const ManagerLayout = (props) => {
  const { t, authUser, children } = props;
  const scope = useManagerScope(authUser);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // La carte de compte dit qui est connecté. Quand le serveur a renvoyé le
  // périmètre, elle nomme l'établissement — c'est l'information utile, et elle
  // sert de bouton de bascule en multi-établissement. Sinon elle retombe sur le
  // nom du manager : c'est une carte de compte, pas un champ « résidence », et
  // ce nom-là n'est jamais imprimé sur une étiquette.
  const establishment = scope.establishmentName || authUser?.user_name || "—";

  const handleSelect = (id) => {
    scope.select(id);
    setSwitcherOpen(false);
  };

  return (
    <div className="mgr">
      <aside className="mgr-aside">
        <div className="mgr-brand">
          <div className="mgr-brand-mark">
            <Icon name="water-outline" />
          </div>
          <div className="mgr-brand-name">WOSH</div>
        </div>

        <nav className="mgr-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              exact={item.exact}
              className="mgr-nav-item"
              activeClassName="is-active"
            >
              <Icon name={item.icon} />
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="mgr-aside-foot">
          <LanguagePicker />

          {switcherOpen && scope.hasMultiple ? (
            <div className="mgr-cp-list">
              {scope.collectionPoints.map((point) => (
                <button
                  key={point.collection_point_id ?? point.name}
                  type="button"
                  className={`mgr-cp-option${
                    point.collection_point_id === scope.collectionPointId ? " is-active" : ""
                  }`}
                  onClick={() => handleSelect(point.collection_point_id)}
                >
                  {point.name}
                </button>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            className={`mgr-account${scope.hasMultiple ? "" : " is-static"}`}
            onClick={scope.hasMultiple ? () => setSwitcherOpen((open) => !open) : undefined}
            aria-expanded={scope.hasMultiple ? switcherOpen : undefined}
          >
            <div className="mgr-avatar">{initialsOf(establishment)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="mgr-account-name">{establishment}</div>
              <div className="mgr-account-role">{t("Beheerder")}</div>
            </div>
            {scope.hasMultiple ? (
              <Icon
                name={switcherOpen ? "chevron-up" : "chevron-down"}
                style={{ color: "rgba(255,255,255,.7)", fontSize: 16 }}
              />
            ) : null}
          </button>

          <button type="button" className="mgr-signout" onClick={() => props.logout(props)}>
            <Icon name="log-out-outline" style={{ fontSize: 18 }} />
            {t("logout")}
          </button>
        </div>
      </aside>

      <main className="mgr-main">
        {typeof children === "function" ? children(scope) : children}
      </main>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, { logout })
)(ManagerLayout);
